import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import PersonIcon from '@mui/icons-material/Person';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetAccountsQuery } from '../../api/recalc/endpoints/accountsApiSlice';
import { getCookie, setCookie } from '../../auth/handleCookie';
import { setAllAccounts } from '../../redux/allAccountsSlice';
import {
  getSelectedAccount,
  setSelectedAccount,
} from '../../redux/selectedAccountSlice';
import { getHostname } from '../../utils/getHostname';

import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

// Custom Popper for dynamic dropdown width
const StyledPopper = styled(Popper)(({ theme }) => ({
  width: 'auto', // Allow the dropdown to adapt to content width
  maxWidth: 'none', // Remove any imposed width restrictions
  zIndex: theme.zIndex.modal, // Ensure proper z-index stacking
}));

import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { withAccount } from '@/utils/withAccount';
import { useGetSessionQuery } from '@/api/apiSlice';

const extractAccountIdFromGuiState = (guiState) => {
  const params = new URLSearchParams(guiState?.replace(/&/g, '&'));
  return params.get('AccountId');
};

const updateGuiStateCookie = (newAccountId) => {
  const guiState = getCookie(`${getHostname()}_GuiState`);
  const params = new URLSearchParams(guiState?.replace(/&/g, '&'));
  params.set('AccountId', newAccountId);
  const newGuiState = Array.from(params.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  setCookie(`${getHostname()}_GuiState`, newGuiState, { path: '/' });
};

const AccountSelect = () => {
  const {
    data: accounts,
    isLoading: isLoadingAccounts,
    isSuccess: accountsIsSuccess,
  } = useGetAccountsQuery();
  const [currentAccount, setCurrentAccount] = React.useState(null);
  let { accountId } = useParams();
  if (accountId === undefined || accountId === null) {
    const guiState = getCookie(`${getHostname()}_GuiState`);
    accountId = extractAccountIdFromGuiState(guiState);
  }
  const selectedAccount = useSelector(getSelectedAccount);
  const { data } = useGetSessionQuery();
  const isAdmin = data?.claims?.['accounts.superuser'] === 'True' || false;
  const textWidthRef = useRef(null); // Reference to measure the text width
  const [inputWidth, setInputWidth] = useState(150); // Default width for the input
  const navigate = useNavigate();
  const location = useLocation();
  const handleClick = () => {
    dispatch(setSelectedAccount(accounts[0]));
    setCurrentAccount(accounts[0]);
  };

  const dispatch = useDispatch();

  const { t } = useTranslation('glossary');
  useEffect(() => {
    if (accountsIsSuccess && accounts) {
      const initialAccount = accounts.find(
        (account) => account.AccountId.toString() === accountId,
      );
      setCurrentAccount(initialAccount ?? accounts[0]);
      dispatch(setAllAccounts(accounts));
      dispatch(setSelectedAccount(initialAccount ?? accounts[0]));
    }
  }, [accounts, accountsIsSuccess]);

  useEffect(() => {
    if (selectedAccount.AccountId !== currentAccount?.AccountId) {
      setCurrentAccount(selectedAccount);
      updateGuiStateCookie(selectedAccount.AccountId);
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (selectedAccount) {
      // Measure the width of the selected option
      const width = textWidthRef.current?.offsetWidth || 150;
      setInputWidth(Math.max(width + 150, 150)); // Add padding, set minimum width
    }
  }, [selectedAccount]);

  const filterOptions = (options, { inputValue }) => {
    if (!inputValue) return options;
    const lowerInput = inputValue.toLowerCase();
    const startsWith = [];
    const includes = [];
    options?.forEach((option) => {
      const name = option.Name.toLowerCase();
      const id = option.AccountId.toString();
      if (name.startsWith(lowerInput) || id.startsWith(inputValue)) {
        startsWith.push(option);
      } else if (name.includes(lowerInput) || id.includes(inputValue)) {
        includes.push(option);
      }
    });
    return [...startsWith, ...includes];
  };

  const handleAccountSelect = (event, value) => {
    setCurrentAccount(value);
    if (value) {
      dispatch(setSelectedAccount(value));
      updateGuiStateCookie(value.AccountId);
    }
    navigate(withAccount(value.AccountId, location.pathname));
  };

  return (
    <>
      <span
        ref={textWidthRef}
        style={{
          visibility: 'hidden',
          position: 'absolute',
          whiteSpace: 'nowrap',
        }}
      >
        {selectedAccount?.Name || ''}
      </span>
      <Autocomplete
        disablePortal
        className="element"
        width="auto"
        blurOnSelect
        options={accounts || []}
        PopperComponent={StyledPopper} // Use custom Popper
        value={selectedAccount?.AccountId ? selectedAccount : null}
        componentsProps={{ popper: { style: { width: 'fit-content' } } }}
        filterOptions={filterOptions}
        onChange={handleAccountSelect}
        getOptionLabel={(option) => (option?.Name || '').toString()}
        disableClearable={true}
        disabled={isLoadingAccounts}
        renderOption={(props, option) => (
          <li {...props} key={option.AccountId}>
            {option.Name}
          </li>
        )}
        sx={{
          width: inputWidth,
          maxWidth: '100%',
          mr: 1,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: '10px',
              },
              '& .MuiInputBase-root:hover': {
                borderRadius: '10px',
                borderColor: 'divider',
              },
              '& .MuiFilledInput-root': {
                borderRadius: '5px',
              },
              '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after':
                {
                  display: 'none',
                },
              '& .MuiInputLabel-root': {
                color: 'gray',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
              },
            }}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              ...params.InputProps,
              placeholder: t('glossary:account'),
              startAdornment: (
                <InputAdornment position="start">
                  {isAdmin ? (
                    <IconButton
                      size="small"
                      sx={{ borderRadius: 2 }}
                      onClick={handleClick}
                    >
                      <AdminPanelSettingsRoundedIcon sx={{ color: 'gray' }} />
                    </IconButton>
                  ) : (
                    <PersonIcon sx={{ color: 'gray' }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isLoadingAccounts && (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
};

export default AccountSelect;
